import React from 'react';
/**
 * usePasswordChange Hook - Manages password change form state and operations
 */
import { useState, useCallback } from 'react';
import { changeOwnPassword, validatePassword, getStrengthColor } from '../services/passwordService';

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordStrength {
  score: number;
  label: string;
}

interface PasswordMessage {
  type: 'success' | 'error';
  text: string;
}

interface UsePasswordChangeReturn {
  passwordData: PasswordData;
  passwordStrength: PasswordStrength;
  passwordErrors: string[];
  isChangingPassword: boolean;
  passwordMessage: PasswordMessage | null;
  showCurrentPassword: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
  setShowCurrentPassword: (show: boolean) => void;
  setShowNewPassword: (show: boolean) => void;
  setShowConfirmPassword: (show: boolean) => void;
  handlePasswordChange: (field: keyof PasswordData, value: string) => void;
  handleChangePassword: (e: React.FormEvent) => Promise<void>;
  getStrengthColor: (score: number) => string;
}

export function usePasswordChange(): UsePasswordChangeReturn {
  // Password change state
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({ score: 0, label: 'Fraca' });
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<PasswordMessage | null>(null);

  // Password visibility state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handle password field change
  const handlePasswordChange = useCallback((field: keyof PasswordData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    setPasswordMessage(null);

    // Validate new password strength in real-time
    if (field === 'newPassword' && value.length > 0) {
      validatePassword(value)
        .then(result => {
          setPasswordStrength({ score: result.strength_score, label: result.strength_label });
          setPasswordErrors(result.errors);
        })
        .catch(() => {
          setPasswordStrength({ score: 0, label: 'Fraca' });
          setPasswordErrors(['Erro ao validar senha']);
        });
    }
  }, []);

  // Handle password change submission
  const handleChangePassword = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Preencha todos os campos' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'As senhas não coincidem' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'A senha deve ter no mínimo 8 caracteres' });
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await changeOwnPassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordMessage({ type: 'success', text: response.message || 'Senha alterada com sucesso!' });

      // Clear form
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordStrength({ score: 0, label: 'Fraca' });
      setPasswordErrors([]);
    } catch (error: any) {
      setPasswordMessage({ type: 'error', text: error.message || 'Erro ao alterar senha' });
    } finally {
      setIsChangingPassword(false);
    }
  }, [passwordData]);

  return {
    passwordData,
    passwordStrength,
    passwordErrors,
    isChangingPassword,
    passwordMessage,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,
    setShowCurrentPassword,
    setShowNewPassword,
    setShowConfirmPassword,
    handlePasswordChange,
    handleChangePassword,
    getStrengthColor,
  };
}
